import { useState } from "react";
import { Button } from "./button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./table";
import { Badge } from "./badge";
import { Info, Upload, FileSpreadsheet, Download, AlertCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

/**
 * ExcelUploadHelper - Componente per upload Excel con preview struttura colonne
 * 
 * @param {Object} props
 * @param {Array} props.columns - Array di oggetti { name: string, required: bool, description: string, example: string }
 * @param {Function} props.onUpload - Callback quando viene selezionato un file
 * @param {string} props.templateUrl - URL opzionale per scaricare il template
 * @param {string} props.title - Titolo del dialog (default: "Struttura File Excel")
 * @param {boolean} props.uploading - Stato di caricamento
 * @param {string} props.buttonText - Testo del pulsante (default: "Importa Excel")
 * @param {string} props.buttonVariant - Variante del pulsante (default: "outline")
 */
export function ExcelUploadHelper({ 
  columns, 
  onUpload, 
  templateUrl,
  title = "Struttura File Excel",
  uploading = false,
  buttonText = "Importa Excel",
  buttonVariant = "outline",
  buttonSize = "default",
  buttonClassName = "",
  children
}) {
  const [showStructure, setShowStructure] = useState(false);
  const [fileInputRef, setFileInputRef] = useState(null);

  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) {
      onUpload(e);
      setShowStructure(false);
    }
    // Reset input per permettere di selezionare lo stesso file
    e.target.value = '';
  };

  const triggerFileInput = () => {
    fileInputRef?.click();
  };

  return (
    <>
      <div className="flex items-center gap-1">
        {/* Pulsante principale upload */}
        <Button 
          variant={buttonVariant} 
          size={buttonSize}
          onClick={() => setShowStructure(true)}
          disabled={uploading}
          className={buttonClassName}
        >
          {uploading ? (
            <>
              <span className="animate-spin mr-2">⏳</span>
              Caricamento...
            </>
          ) : (
            children || (
              <>
                <Upload className="h-4 w-4 mr-2" />
                {buttonText}
              </>
            )
          )}
        </Button>

        {/* Input file nascosto */}
        <input
          type="file"
          ref={setFileInputRef}
          className="hidden"
          accept=".xlsx,.xls"
          onChange={handleFileSelect}
        />
      </div>

      {/* Dialog con struttura colonne */}
      <Dialog open={showStructure} onOpenChange={setShowStructure}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              {title}
            </DialogTitle>
            <DialogDescription>
              Il file Excel deve contenere le seguenti colonne (la prima riga deve essere l'intestazione):
            </DialogDescription>
          </DialogHeader>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">Colonna</TableHead>
                  <TableHead className="font-semibold w-24 text-center">Obbligatorio</TableHead>
                  <TableHead className="font-semibold">Descrizione</TableHead>
                  <TableHead className="font-semibold">Esempio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {columns.map((col, idx) => (
                  <TableRow key={idx} className={col.required ? "bg-amber-50/30" : ""}>
                    <TableCell className="font-mono text-sm font-medium">
                      {col.name}
                    </TableCell>
                    <TableCell className="text-center">
                      {col.required ? (
                        <Badge variant="destructive" className="text-xs">Sì</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">No</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {col.description}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {col.example}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg text-sm">
            <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-blue-800 dark:text-blue-200">
              <strong>Suggerimenti:</strong>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>La prima riga deve contenere i nomi delle colonne</li>
                <li>I campi obbligatori devono essere sempre compilati</li>
                <li>Date nel formato: YYYY-MM-DD o DD/MM/YYYY</li>
                <li>Importi senza simbolo valuta (es: 1234.56)</li>
              </ul>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            {templateUrl && (
              <Button 
                variant="outline" 
                onClick={() => window.open(templateUrl, '_blank')}
                className="mr-auto"
              >
                <Download className="h-4 w-4 mr-2" />
                Scarica Template
              </Button>
            )}
            <Button variant="ghost" onClick={() => setShowStructure(false)}>
              Annulla
            </Button>
            <Button onClick={triggerFileInput} disabled={uploading}>
              <Upload className="h-4 w-4 mr-2" />
              Seleziona File
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Pulsante compatto con icona info per mostrare la struttura
 */
export function ExcelStructureInfo({ columns, title }) {
  const [showInfo, setShowInfo] = useState(false);

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => setShowInfo(true)}
            >
              <Info className="h-4 w-4 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Visualizza struttura Excel</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={showInfo} onOpenChange={setShowInfo}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              {title || "Struttura File Excel"}
            </DialogTitle>
          </DialogHeader>

          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">Colonna</TableHead>
                  <TableHead className="font-semibold w-24 text-center">Obbl.</TableHead>
                  <TableHead className="font-semibold">Descrizione</TableHead>
                  <TableHead className="font-semibold">Esempio</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {columns.map((col, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono text-sm font-medium">{col.name}</TableCell>
                    <TableCell className="text-center">
                      {col.required ? (
                        <Badge variant="destructive" className="text-xs">Sì</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">No</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{col.description}</TableCell>
                    <TableCell className="font-mono text-xs">{col.example}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowInfo(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Definizioni colonne per i vari tipi di import
export const EXCEL_COLUMNS = {
  fatture: [
    { name: "tipo_documento", required: true, description: "Tipo: FV (vendita) o FA (acquisto)", example: "FV" },
    { name: "data", required: true, description: "Data fattura", example: "2024-01-15" },
    { name: "numero", required: true, description: "Numero fattura", example: "001/2024" },
    { name: "fornitore_cliente", required: true, description: "Ragione sociale", example: "Azienda SRL" },
    { name: "imponibile", required: true, description: "Importo imponibile", example: "1000.00" },
    { name: "iva", required: false, description: "Importo IVA", example: "220.00" },
    { name: "totale", required: true, description: "Totale fattura", example: "1220.00" },
    { name: "data_scadenza", required: false, description: "Data scadenza pagamento", example: "2024-02-15" },
  ],
  movimenti_bancari: [
    { name: "banca", required: false, description: "Nome banca (match automatico con banche esistenti)", example: "Intesa Sanpaolo" },
    { name: "data", required: true, description: "Data contabile", example: "2024-01-15" },
    { name: "data_valuta", required: false, description: "Data valuta", example: "2024-01-16" },
    { name: "descrizione", required: true, description: "Descrizione movimento", example: "Bonifico da cliente" },
    { name: "importo", required: true, description: "Importo (positivo=entrata, negativo=uscita)", example: "-500.00" },
    { name: "tipo", required: false, description: "Tipo: entrata o uscita", example: "uscita" },
  ],
  movimenti_carta: [
    { name: "data_operazione", required: true, description: "Data operazione", example: "2024-01-15" },
    { name: "data_valuta", required: false, description: "Data valuta", example: "2024-01-16" },
    { name: "descrizione", required: true, description: "Descrizione", example: "Pagamento carburante" },
    { name: "importo", required: true, description: "Importo", example: "50.00" },
    { name: "intestatario", required: false, description: "Intestatario carta (deve corrispondere al nome in anagrafica carte)", example: "Mario Rossi" },
    { name: "esercente", required: false, description: "Nome esercente", example: "Eni Station" },
    { name: "categoria", required: false, description: "Categoria spesa", example: "Carburante" },
  ],
  conti: [
    { name: "codice", required: true, description: "Codice conto univoco", example: "60.10.001" },
    { name: "nome", required: true, description: "Nome del conto", example: "Costi carburante" },
    { name: "tipo", required: true, description: "Tipo: costo, ricavo, attivo, passivo", example: "costo" },
    { name: "categoria", required: false, description: "Categoria di appartenenza", example: "Costi operativi" },
    { name: "descrizione", required: false, description: "Descrizione estesa", example: "Spese per carburante automezzi" },
  ],
  scadenze: [
    { name: "Segno", required: false, description: "Segno contabile", example: "" },
    { name: "Data", required: true, description: "Data scadenza (gg-mm-aaaa)", example: "01-01-2026" },
    { name: "Ragione sociale", required: true, description: "Ragione sociale del fornitore/cliente", example: "Grenke Italia S.p.A" },
    { name: "Descrizione", required: true, description: "Descrizione con rif. fattura (es. Fatt. N.XXX del GG-MM-AAAA)", example: "Fatt. N.0001060890/2025 del 17-12-2025" },
    { name: "Pagamento", required: false, description: "Modalita di pagamento", example: "Bonifico bancario" },
    { name: "Attivo", required: false, description: "Importo attivo (credito/entrata)", example: "12200.00" },
    { name: "Passivo", required: false, description: "Importo passivo (debito/uscita)", example: "193.98" },
  ],
  scadenze_ricorrenti: [
    { name: "descrizione", required: true, description: "Descrizione scadenza", example: "Canone affitto" },
    { name: "importo", required: true, description: "Importo", example: "800.00" },
    { name: "giorno_scadenza", required: true, description: "Giorno del mese (1-28)", example: "5" },
    { name: "frequenza", required: true, description: "mensile, bimestrale, trimestrale, annuale", example: "mensile" },
    { name: "data_inizio", required: true, description: "Data inizio validità", example: "2024-01-01" },
    { name: "data_fine", required: false, description: "Data fine validità", example: "2024-12-31" },
    { name: "beneficiario", required: false, description: "Beneficiario", example: "Proprietario immobile" },
    { name: "categoria", required: false, description: "Categoria", example: "Affitti" },
  ],
  leasing_rates: [
    { name: "Numero Rata", required: false, description: "Numero progressivo della rata (auto-generato se assente)", example: "1" },
    { name: "Data Scadenza", required: false, description: "Data scadenza rata", example: "2025-01-15" },
    { name: "Importo", required: true, description: "Importo canone della rata", example: "1250.00" },
    { name: "Quota Capitale", required: false, description: "Parte capitale della rata", example: "950.00" },
    { name: "Quota Interessi", required: false, description: "Parte interessi della rata", example: "300.00" },
    { name: "Note", required: false, description: "Note aggiuntive", example: "Maxicanone" },
  ],
};

export default ExcelUploadHelper;
